<script setup lang="ts">
import { computed, inject, ref, watchEffect, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { moreCountBuffer } from '@lib/constants/more.const'
import CTag from '@lib/components/CTag/CTag.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTableEditActionButtons from '@lib/components/CTableEditActionButtons/CTableEditActionButtons.vue'
import ClickOutsideDirective from '@lib/directives/clickOutside'
import useThemeVars from '@lib/composables/useThemeVars'
import { HIDE_TAGS_WITH_PREFIX } from '@lib/constants/conventions.const'
import { highlight } from '@lib/util/string'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = withDefaults(
  defineProps<{
    modelValue: string[]
    search?: string
    state?: 'view' | 'edit'
    readonly?: boolean
    placeholder?: string
    maxTagsToShow?: number
    popoverTo?: string | false
    rowIndex?: number
    tagsClass?: string
    moreTagsClass?: string
    displayAsText?: boolean
    suggestedTagsFetcher?: (search: string) => Promise<string[]> | string[]
  }>(),
  {
    search: '',
    state: 'view',
    maxTagsToShow: 2,
    popoverTo: 'body',
    rowIndex: 0,
    displayAsText: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'edit'): void
  (e: 'cancel'): void
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const themeVars = useThemeVars()
const vClickOutside = ClickOutsideDirective

const tagsRef = ref<InstanceType<typeof CTags> | null>(null)
const model = ref(props.modelValue)

watchEffect(() => {
  if (props.state !== 'edit') {
    model.value = props.modelValue
  }
})

const enableShortTagColumn = computed(() => {
  return launchDarkly.value.enable_short_tag_column_in_tables ?? false
})

// Method to determine if tooltip should be shown
const shouldShowTagTooltip = (tag: string): boolean => {
  // Only show tooltips when short tag column is enabled and tag is long enough
  if (enableShortTagColumn.value) {
    return tag.length > 20
  }

  // No tooltip for regular display
  return false
}

const tags = computed(() => {
  const tags = model.value || []
  const filteredTags = tags.filter((t) => !HIDE_TAGS_WITH_PREFIX.some((prefix) => t.startsWith(prefix)))

  if (props.maxTagsToShow === 0 || filteredTags.length <= props.maxTagsToShow + moreCountBuffer) {
    return filteredTags
  }

  return filteredTags.slice(0, props.maxTagsToShow)
})

const remainingTags = computed(() => {
  if (props.maxTagsToShow === 0) {
    return []
  }
  return (
    model.value
      ?.slice?.(tags.value.length)
      ?.filter?.((t) => !HIDE_TAGS_WITH_PREFIX.some((prefix) => t.startsWith(prefix))) ?? []
  )
})

function deepFindInput(nodeList: HTMLCollection): HTMLInputElement | null {
  let inputElement: HTMLInputElement | null = null

  Array.from(nodeList).some((node: Element) => {
    if (node instanceof HTMLElement && node.tagName === 'INPUT') {
      inputElement = node as HTMLInputElement
      return true
    }

    inputElement = node instanceof HTMLElement ? deepFindInput(node.children) : null

    return inputElement !== null
  })

  return inputElement
}

function onSave(e?: Event) {
  if (
    e?.target instanceof HTMLElement &&
    (e.target.classList.contains('n-base-select-option--show-checkmark') ||
      e.target.classList.contains('n-base-select-option__content'))
  )
    return

  emit('update:modelValue', model.value)
}

function onCancel() {
  model.value = props.modelValue
  emit('cancel')
}

function handleKey(event: KeyboardEvent) {
  switch (event.key) {
    case 'Enter': {
      const inputElement = deepFindInput(tagsRef.value!.$el.children) as HTMLInputElement
      if (inputElement.value) return
      onSave()
      break
    }

    case 'Escape':
      onCancel()
      break
    default:
      break
  }
}
</script>

<template>
  <div class="c-table-tag">
    <div v-if="state === 'view'" class="flex items-center c-table-tag--view">
      <div v-if="tags.length" class="c-table-tag--view__tags flex flex-wrap items-center truncate gap-1">
        <div
          v-if="displayAsText"
          class="font-size-[14px] tracking-[0.4px] leading-normal color-text2 whitespace-pre-wrap"
        >
          <span v-html="highlight(tags.join(', '), search)" />
        </div>
        <template v-else>
          <CTag
            v-for="(tag, index) in tags"
            :key="index"
            v-tooltip="shouldShowTagTooltip(tag) ? tag : null"
            :class="tagsClass"
            size="small"
          >
            {{ tag }}
          </CTag>
        </template>

        <CPopover
          v-if="remainingTags.length"
          class="c-table-tag__popover max-w-[600px]"
          :placement="rowIndex === 0 ? 'bottom' : 'top'"
          :to="popoverTo"
          trigger="click"
        >
          <template #trigger>
            <div
              v-if="enableShortTagColumn"
              class="underline underline-solid cursor-pointer select-none c-table-tag--view__more-tags flex-shrink-0"
              @click.stop
            >
              + {{ remainingTags.length }}
            </div>
            <div
              v-else
              class="underline underline-solid cursor-pointer select-none truncate c-table-tag--view__more-tags"
              @click.stop
            >
              {{
                t('canvasUI.components.tables.andCountMore', {
                  count: remainingTags.length,
                })
              }}
            </div>
          </template>
          <div class="flex flex-wrap items-center gap-1 truncate c-table-tag__popover-tags">
            <div
              v-if="displayAsText"
              class="font-size-[14px] tracking-[0.4px] leading-normal color-text2 whitespace-pre-wrap"
            >
              {{ remainingTags.join(', ') }}
            </div>
            <template v-else>
              <CTag v-for="(tag, index) in remainingTags" :key="index" :class="moreTagsClass" size="small">
                {{ tag }}
              </CTag>
            </template>
          </div>
        </CPopover>
      </div>
      <template v-if="!readonly">
        <div
          v-if="!tags.length"
          class="c-table-tag--view__no-tags edit-trigger color-primary3 cursor-pointer"
          @click.stop="emit('edit')"
        >
          {{ t('canvasUI.components.tables.addTags') }}
        </div>
        <CIcon
          class="edit-trigger cursor-pointer px-4 trigger-edit"
          :color="themeVars.primary3"
          icon="pen"
          @click.stop="emit('edit')"
        />
      </template>
    </div>

    <div
      v-else-if="state === 'edit'"
      class="flex flex-1 items-center gap-[2px] w-full c-table-tag--edit"
      @keydown="handleKey"
    >
      <CTags
        ref="tagsRef"
        v-model:value="model"
        v-click-outside="onSave"
        autofocus
        input-same-row
        min-auto-complete-width="105px"
        :suggested-tags-fetcher="suggestedTagsFetcher"
      />
      <CTableEditActionButtons @cancel="onCancel" @save="onSave" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.c-table-tag {
  .edit-trigger {
    visibility: hidden;
  }

  &:hover {
    .edit-trigger {
      visibility: visible;
    }
  }

  &--view {
    &__tags,
    &__more-tags {
      font-size: 14px;
      line-height: normal;
      letter-spacing: 0.4px;
    }
  }
}
</style>
